/**
 * Profile API client — wraps GET /profile and PUT /profile REST calls.
 *
 * Uses Amplify fetchAuthSession to attach the Cognito JWT Authorization header.
 * REST_API_URL sourced from VITE_REST_API_URL env var (same as other REST calls).
 *
 * Security: Authorization header carries the Cognito ID token.
 * The server extracts sub from JWT claims — never from request body.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import type { UserProfile } from '../types/profile';

export type { UserProfile };

const REST_BASE = ((import.meta.env.VITE_REST_API_URL as string | undefined) ?? '').replace(/\/$/, '');

async function getAuthHeader(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return `Bearer ${token}`;
}

/**
 * Fetches the user's profile. Returns null if not yet created (404).
 */
export async function fetchProfile(): Promise<UserProfile | null> {
  const auth = await getAuthHeader();
  const res = await fetch(`${REST_BASE}/profile`, {
    method: 'GET',
    headers: { Authorization: auth },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /profile failed: ${res.status}`);
  const data = (await res.json()) as { profile: UserProfile };
  return data.profile;
}

export interface ResumeUploadUrlResponse {
  uploadUrl: string;
  key: string;
  expiresAt: number;
}

export async function requestResumeUploadUrl(
  filename: string,
  contentType: string,
): Promise<ResumeUploadUrlResponse> {
  const auth = await getAuthHeader();
  const res = await fetch(`${REST_BASE}/profile/resume-upload-url`, {
    method: 'POST',
    headers: { Authorization: auth, 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, contentType }),
  });
  if (!res.ok) throw new Error(`Failed to get resume upload URL (${res.status})`);
  return res.json() as Promise<ResumeUploadUrlResponse>;
}

export interface ProcessResumeResponse {
  resumeCareerSummary: string;
  processedAt: string;
}

export async function processResume(): Promise<ProcessResumeResponse> {
  const auth = await getAuthHeader();
  const res = await fetch(`${REST_BASE}/profile/process-resume`, {
    method: 'POST',
    headers: { Authorization: auth },
  });
  if (!res.ok) throw new Error(`POST /profile/process-resume failed: ${res.status}`);
  return res.json() as Promise<ProcessResumeResponse>;
}

export async function deleteResume(): Promise<void> {
  const auth = await getAuthHeader();
  const res = await fetch(`${REST_BASE}/profile/resume`, {
    method: 'DELETE',
    headers: { Authorization: auth },
  });
  if (!res.ok && res.status !== 204) throw new Error(`DELETE /profile/resume failed: ${res.status}`);
}

/**
 * Upserts the user's profile. Returns on success, throws on error.
 */
export async function saveProfile(profile: UserProfile): Promise<void> {
  const auth = await getAuthHeader();
  const res = await fetch(`${REST_BASE}/profile`, {
    method: 'PUT',
    headers: {
      Authorization: auth,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(profile),
  });
  if (!res.ok) throw new Error(`PUT /profile failed: ${res.status}`);
}
